import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api from '../lib/api';
import FounderBadge from './FounderBadge';

// Landing page shows a bounded preview of the wall (marquee) — the full,
// scrollable list lives on the dedicated /founders page.
const LANDING_LIMIT = 24;

/**
 * FounderWall — public Landing page social-proof strip.
 *
 * Fetches the earliest N launch-era travelers from `/api/founders` and renders
 * two marquee rows of avatar tiles scrolling in opposite directions. Each tile
 * shows the traveler's avatar (or initials fallback), display name, country
 * flag, cosmic rank, and a mini Founder Badge — telegraphing that anyone who
 * signs up during launch joins them on the wall.
 *
 * The endpoint is public (no auth) so this works before a visitor signs up —
 * that's the whole point: they see who's already inside and want in.
 */
function TileRow({ items, direction = 'left', speed = 60 }) {
  // Duplicate the list so the translate loop wraps seamlessly
  const doubled = items.length ? [...items, ...items] : [];
  return (
    <div className="relative overflow-hidden" style={{ maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)' }}>
      <div
        className="flex gap-3 py-2 will-change-transform"
        style={{
          width: 'max-content',
          animation: `founder-wall-scroll-${direction} ${speed}s linear infinite`,
        }}
        data-testid={`founder-row-${direction}`}
      >
        {doubled.map((u, i) => (
          <Tile key={`${direction}-${i}-${u.user_id}`} user={u} />
        ))}
      </div>
    </div>
  );
}

function Tile({ user }) {
  const initials = (user.displayName || user.username || 'FV').slice(0, 2).toUpperCase();
  return (
    <div className="shrink-0 w-32 rounded-2xl bg-white/5 border border-white/10 hover:border-amber-400/40 backdrop-blur-sm p-2.5 flex flex-col items-center transition group" data-testid={`founder-tile-${user.user_id}`}>
      <div className="relative">
        <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-fuchsia-500/40 to-cyan-500/40 border-2 border-white/15 group-hover:border-amber-400/60 flex items-center justify-center transition">
          {user.avatar
            ? <img src={user.avatar} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            : <span className="text-sm font-bold text-white/90">{initials}</span>}
        </div>
        <FounderBadge size="sm" className="absolute -bottom-1 -right-1" />
      </div>
      <div className="mt-1.5 text-[11px] font-semibold text-white/90 truncate w-full text-center">{user.displayName || user.username}</div>
      <div className="text-[10px] text-white/45 flex items-center gap-1">
        <span>{user.flag || '🌍'}</span>
        <span className="uppercase tracking-wider" style={{ color: user.activity_color }}>Rank {user.rank || 1}</span>
      </div>
    </div>
  );
}

export default function FounderWall() {
  const [data, setData] = useState({ total: 0, founders: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    // Fetch a small preview slice for the landing marquee. The dedicated
    // /founders page fetches its own larger page independently.
    api.get(`/founders?limit=${LANDING_LIMIT}`)
      .then(r => { if (alive) setData(r.data || { total: 0, founders: [] }); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Hide the section entirely if we somehow have zero founders (fresh install)
  if (!loading && data.founders.length === 0) return null;

  // Split into two rows so both sides of the wall are populated
  const half = Math.ceil(data.founders.length / 2);
  const rowA = data.founders.slice(0, half);
  const rowB = data.founders.slice(half);

  return (
    <section className="relative z-10 py-16 sm:py-20" data-testid="founder-wall">
      <style>{`
        @keyframes founder-wall-scroll-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes founder-wall-scroll-right {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
      `}</style>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-3 flex items-center justify-center gap-2">
            <FounderBadge size="sm" /> The Founder Wall
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-fuchsia-300 to-cyan-300">
              {loading ? '…' : data.total.toLocaleString()}
            </span>
            <span className="text-white"> launch travelers already inside</span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/60 max-w-2xl mx-auto">
            These are the earliest travelers who joined WorldVibe and chose to be listed publicly.
            Waitlist members who convert during launch week receive a permanent{' '}
            <span className="text-amber-300 font-semibold">Founder Badge</span> that lives on their
            profile forever — no one else can earn it again.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center gap-3 py-8">
            {[0,1,2,3,4,5].map(i => (
              <div key={i} className="w-32 h-24 rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {rowA.length > 0 && <TileRow items={rowA} direction="left" speed={70} />}
            {rowB.length > 0 && <TileRow items={rowB} direction="right" speed={90} />}
          </div>
        )}

        {/* Meaningful footer — three concrete stats + a soft "See all"
            link. No more "Claim your spot" CTA (that lives in the hero
            already; two of the same button dilutes both). */}
        <div className="mt-8 max-w-3xl mx-auto grid grid-cols-3 gap-3 text-center">
          <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-3" data-testid="founder-wall-stat-count">
            <div className="font-display font-bold text-xl text-amber-300">{data.total.toLocaleString()}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">Travelers on the wall</div>
          </div>
          <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-3">
            <div className="font-display font-bold text-xl text-fuchsia-300">Permanent</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">Badge never expires</div>
          </div>
          <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-3">
            <div className="font-display font-bold text-xl text-cyan-300">Opt-in only</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">Privacy-first display</div>
          </div>
        </div>

        {data.total > LANDING_LIMIT && (
          <div className="text-center mt-6">
            <Link
              to="/founders"
              data-testid="founder-wall-view-all"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/12 transition"
            >
              View all {data.total.toLocaleString()} founders <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
